import {
	Controller,
	useFormContext,
	useWatch,
} from "react-hook-form";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import Grid from "@mui/material/Grid2";
import CalculationMethodTypeSelect from "view/calculationmethods/components/select/CalculationMethodTypeSelect";
import { Fragment, ReactNode } from "react";
import { AgreementTypeEnum, CalculationMethodType } from "__generated__/graphql";
import FormField from "components/cck/FormField";
import { useSchema } from "@schemas/calculationmethods/useSchema";
import { useSchema as useAgreementSchema } from "@schemas/agreements/useSchema";
import { AgreementSchema } from "view/agreements/schema";
import { UpdateAgreement } from "view/agreements/AgreementCreateEdit";
import { CalculationMethodSchema } from "view/calculationmethods/schema";
import { AgreementType } from "view/agreementtypes/types/AgreementType";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

type CalculationMethodFormProps = {
	index: number;
	currency: CurrencyFragment;
	updateAgreement: UpdateAgreement;
	agreementTypes?: AgreementType[];
	customAgreementTypes?: AgreementTypeEnum[];
}

type FormValues = {
	agreements: {
		id?: number;
		type: AgreementTypeEnum | null;
		calculationMethod: {
			type: CalculationMethodType | null;
		}
	}[];
};

type FormField = {
	id: string;
	render: () => ReactNode;
}

const CalculationMethodForm = ({ agreementTypes, customAgreementTypes, index, currency, updateAgreement }: CalculationMethodFormProps) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();

	const type = useWatch({ control, name: `agreements.${index}.type` });
	const calculationMethodType = useWatch({ control, name: `agreements.${index}.calculationMethod.type` });
	const agreements = useWatch({ control, name: "agreements" }) ?? [];

	const agreementSchema = useAgreementSchema(type);
	const schema = useSchema(calculationMethodType);

	const fields = {
		...(schema?.fields ?? {}),
		...(agreementSchema?.calculationMethodFields ?? {}),
	};

	const hiddenFields = Object.entries(fields ?? {}).filter(([ , x ]) => {
		const rules = x.rules;
		return rules?.hidden || rules?.hiddenIf?.({ calculationMethodType });
	}).map(([ id ]) => id);

	const setDefaultValues = (methodType: CalculationMethodType | null) => {
		const agreementSchemaPerType = type ? AgreementSchema[type as AgreementTypeEnum] : undefined;
		const agreementTypeDefaults = agreementSchemaPerType?.defaultValues?.calculationMethod ?? {};

		const schemaPerType = methodType ? CalculationMethodSchema[methodType] : undefined;
		const typeDefaults = schemaPerType?.defaultValues ?? {};

		const agreement = agreements?.[index] ?? {};

		updateAgreement(index, {
			...agreement,
			manualActionRequired: methodType === CalculationMethodType.Manual,
			calculationMethod: {
				...typeDefaults,
				...agreementTypeDefaults,
				type: methodType,
			},
		});
	};

	const CalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.type",
			render: () => type && (
				<Grid size={{ md: 4, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.type`}>
						<FormGroup label={intl.formatMessage({ id: "CALCULATION_METHOD_TYPE" })} required>
							<Controller
								rules={{ required: true }}
								control={control}
								render={({ field: { onChange, value } }) => (
									<CalculationMethodTypeSelect
										agreementType={type}
										value={value}
										onChange={(v) => {
											onChange(v);
											setDefaultValues(v ?? null);
										}}
									/>
								)}
								name={`agreements.${index}.calculationMethod.type`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod",
			render: () => type && schema?.Form && (
				<schema.Form
					agreementTypes={agreementTypes}
					customAgreementTypes={customAgreementTypes}
					hiddenFields={hiddenFields}
					index={index}
					currency={currency}
				/>
			),
		},
	];

	return (
		<>
			{CalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default CalculationMethodForm;
